import { useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import toLower from 'lodash/toLower';
import debounce from 'lodash/debounce';
import { keyEventToHotkey, DropdownHotkey } from '../../hotkeys';
import { DropdownStateContext } from '../../DropdownStateManager';
import { mapDropdownItemsToIndexes } from './utils';
import { StandardMenuType } from './types';
import StandardMenuContext from './context';
export var NavigationDirection = /*#__PURE__*/ function(NavigationDirection) {
    NavigationDirection["UP"] = "up";
    NavigationDirection["DOWN"] = "down";
    return NavigationDirection;
}({});
/** For use with components that make use of our `DropdownStateContext` and have an element with `role="menu"`  */ export function useMenuHotkeys() {
    const { closeDropdown } = useContext(DropdownStateContext);
    const { menuType } = useContext(StandardMenuContext);
    const handleGlobalHotkeys = useCallback((e)=>{
        switch(keyEventToHotkey(e)){
            case DropdownHotkey.TAB:
                if (menuType === StandardMenuType.DROPDOWN) closeDropdown();
                break;
            case DropdownHotkey.ESCAPE:
                closeDropdown();
                e.preventDefault();
                e.stopPropagation();
                break;
            default:
                break;
        }
    }, [
        closeDropdown,
        menuType
    ]);
    return handleGlobalHotkeys;
}
/**
 * Stores a search phrase that periodically resets. To be used when jumping focus to a menu item based on the provided `searchPhrase`
 *
 * e.g.
 * An item is focused and a user starts typing `m`
 * We want to store that `m` here for a set period of time
 */ export function useMenuSearch() {
    const [searchPhrase, setSearchPhrase] = useState('');
    // Because we clear the search after a few seconds, we need to keep track of the result
    // of the debounced function call and clean up if this unmounts.
    const debouncedClearSearchRef = useRef(undefined);
    const debouncedClearSearchPhrase = useMemo(()=>debounce(()=>{
            setSearchPhrase('');
        }, 1000), [
        setSearchPhrase
    ]);
    const pushLetter = useCallback((letter)=>{
        setSearchPhrase(`${searchPhrase}${letter}`);
        // Return the debounced result in case we need to cancel
        debouncedClearSearchRef.current = debouncedClearSearchPhrase();
    }, [
        searchPhrase,
        setSearchPhrase,
        debouncedClearSearchPhrase
    ]);
    // Make sure we clean up the debounced function if it's hanging around during unmount
    useEffect(()=>{
        var _debouncedClearSearchRef_current;
        return (_debouncedClearSearchRef_current = debouncedClearSearchRef.current) === null || _debouncedClearSearchRef_current === void 0 ? void 0 : _debouncedClearSearchRef_current.cancel;
    }, [
        debouncedClearSearchRef
    ]);
    return {
        searchPhrase,
        pushLetter
    };
}
/**
 * For use with components that make use of our `DropdownStateContext` and have an element with `role="menuitem"`
 *
 * Focus management should be handled by the consumer based on the provided `currentIndex`.
 *
 * @param itemCount How many items are we navigating through
 * @param onPassStart What should the behaviour be when we press `DropdownHotkey.ARROW_UP` while at the start of the list? Default behaviour is to wrap around to the bottom.
 * @param onPassEnd What should the behaviour be when we press `DropdownHotkey.ARROW_DOWN` while at the end of the list? Default behaviour is to wrap around to the top.
 * @param onSearch What should the behaviour be when we type alphanumeric characters while an item is focused?
 */ export function useMenuItemHotkeys(itemCount, { onPassStart, onPassEnd, onSearch, onPressHome, onPressEnd, initialIndex = null } = {}) {
    const { closeDropdown } = useContext(DropdownStateContext);
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [navigationDirection, setNavigationDirection] = useState(null);
    const handleGlobalHotkeys = useMenuHotkeys();
    const handleHotkeys = useCallback((e)=>{
        // By default, wrap around to the end if a user navigates past the first item
        const handlePassStart = onPassStart ? onPassStart : ()=>setCurrentIndex(itemCount - 1);
        // By default, wrap around to the start if a user navigates past the last item
        const handlePassEnd = onPassEnd ? onPassEnd : ()=>setCurrentIndex(0);
        const handlePressHome = onPressHome ? onPressHome : ()=>setCurrentIndex(0);
        const handlePressEnd = onPressEnd ? onPressEnd : ()=>setCurrentIndex(itemCount - 1);
        if (currentIndex !== null) {
            switch(keyEventToHotkey(e)){
                case DropdownHotkey.ARROW_UP:
                    setNavigationDirection("up");
                    if (currentIndex > 0) {
                        setCurrentIndex(currentIndex - 1);
                    } else {
                        handlePassStart();
                    }
                    e.preventDefault();
                    break;
                case DropdownHotkey.ARROW_DOWN:
                    setNavigationDirection("down");
                    if (currentIndex < itemCount - 1) {
                        setCurrentIndex(currentIndex + 1);
                    } else {
                        handlePassEnd();
                    }
                    e.preventDefault();
                    break;
                case DropdownHotkey.HOME:
                    handlePressHome();
                    setNavigationDirection("down");
                    e.preventDefault();
                    break;
                case DropdownHotkey.END:
                    handlePressEnd();
                    setNavigationDirection("up");
                    e.preventDefault();
                    break;
                case DropdownHotkey.SEARCH:
                    if (onSearch) {
                        onSearch(toLower(e.key));
                    }
                    break;
                default:
                    break;
            }
        } else {
            switch(keyEventToHotkey(e)){
                case DropdownHotkey.HOME:
                    setNavigationDirection("down");
                    setCurrentIndex(0);
                    e.preventDefault();
                    break;
                case DropdownHotkey.ARROW_UP:
                    setNavigationDirection("up");
                    setCurrentIndex(0);
                    e.preventDefault();
                    break;
                case DropdownHotkey.END:
                    setNavigationDirection("up");
                    setCurrentIndex(itemCount - 1);
                    e.preventDefault();
                    break;
                case DropdownHotkey.ARROW_DOWN:
                    setNavigationDirection("down");
                    setCurrentIndex(itemCount - 1);
                    e.preventDefault();
                    break;
                case DropdownHotkey.SEARCH:
                    if (onSearch) {
                        onSearch(toLower(e.key));
                    }
                    break;
                case DropdownHotkey.SPACE:
                    closeDropdown();
                    break;
                default:
                    break;
            }
        }
        handleGlobalHotkeys(e);
    }, [
        currentIndex,
        setCurrentIndex,
        itemCount,
        handleGlobalHotkeys,
        onSearch,
        closeDropdown,
        onPassEnd,
        onPassStart,
        onPressEnd,
        onPressHome
    ]);
    return {
        handleKeyboardEvents: handleHotkeys,
        currentIndex,
        setCurrentIndex,
        goToNextIndex: ()=>{
            if (currentIndex !== null) {
                setNavigationDirection("down");
                setCurrentIndex(currentIndex < itemCount - 1 ? currentIndex + 1 : 0);
            }
        },
        goToPreceedingIndex: ()=>{
            if (currentIndex !== null) {
                setNavigationDirection("up");
                setCurrentIndex(currentIndex > 0 ? currentIndex - 1 : itemCount - 1);
            }
        },
        navigationDirection,
        setNavigationDirection
    };
}
/**
 * Provides keyboard support to our StandardMenu components.
 *
 * Modeled after the keyboard UX of a native `<select>` tag.
 */ export function useStandardMenuKeyboardSupport(items) {
    const { menuType } = useContext(StandardMenuContext);
    const { isOpen, firstKeyInteraction } = useContext(DropdownStateContext);
    const itemIndexesByLabels = mapDropdownItemsToIndexes(items);
    const { searchPhrase, pushLetter } = useMenuSearch();
    const { handleKeyboardEvents, currentIndex, setCurrentIndex, goToNextIndex, goToPreceedingIndex, navigationDirection, setNavigationDirection } = useMenuItemHotkeys(items.length, {
        onSearch: pushLetter
    });
    const [groupStartingItemIndex, setGroupStartingItemIndex] = useState(null);
    // Make sure we set the correct starting index based on what we opened the dropdown with.
    useEffect(()=>{
        if (menuType === StandardMenuType.EMBEDDED) {
            return;
        }
        if (isOpen && firstKeyInteraction) {
            switch(firstKeyInteraction){
                case DropdownHotkey.END:
                case DropdownHotkey.ARROW_UP:
                    setNavigationDirection("up");
                    setCurrentIndex(items.length - 1);
                    setGroupStartingItemIndex(null);
                    break;
                case DropdownHotkey.SPACE:
                case DropdownHotkey.ENTER:
                case DropdownHotkey.HOME:
                case DropdownHotkey.ARROW_DOWN:
                    setNavigationDirection("down");
                    setCurrentIndex(0);
                    setGroupStartingItemIndex(null);
                    break;
                default:
                    break;
            }
        } else {
            setCurrentIndex(null);
        }
    }, [
        isOpen,
        firstKeyInteraction,
        setCurrentIndex,
        items.length,
        setNavigationDirection,
        menuType
    ]);
    // Handle setting the current focus based on the searchPhrase
    useEffect(()=>{
        if (isOpen && searchPhrase) {
            const searchRegex = new RegExp(`^${searchPhrase}`);
            const matchedLabel = Object.keys(itemIndexesByLabels).find((value)=>searchRegex.test(toLower(value)));
            if (matchedLabel) {
                const { index, innerIndex } = itemIndexesByLabels[matchedLabel];
                setCurrentIndex(index);
                setGroupStartingItemIndex(innerIndex);
            }
        }
    }, [
        isOpen,
        searchPhrase,
        setCurrentIndex,
        itemIndexesByLabels
    ]);
    // Handle setting the index on open if an item is already selected
    // PART2 TODO: Refine this so that it works more consistently
    useEffect(()=>{
        const matchedItem = Object.values(itemIndexesByLabels).find((item)=>item.selected);
        if (matchedItem && currentIndex === null) {
            const { index, innerIndex } = matchedItem;
            setCurrentIndex(index);
            setGroupStartingItemIndex(innerIndex);
        }
    }, [
        isOpen,
        itemIndexesByLabels,
        setCurrentIndex,
        setGroupStartingItemIndex,
        currentIndex
    ]);
    return {
        /** The index of the item that is currently focused */ currentItemIndex: currentIndex,
        /** The index of an group entry inside a group that is currently focused. Will be null for any item that isn't a group */ groupStartingItemIndex,
        /**
     * A callback to be added to the currently focused item's onKeyDown function.
     * Contains code that handles navigating to other items or closing the menu
     */ handleKeyboardEvents,
        /** Sets currentItemIndex to the preceeding item's index, possibly wrapping around to the end of the list of items. */ focusPreceedingItem: goToPreceedingIndex,
        /** Sets currentItemIndex to the next item's index, possibly wrapping around to the start of the list of items */ focusNextItem: goToNextIndex,
        /** Sets currentItemIndex to the first item's index */ focusFirstItem: ()=>{
            setCurrentIndex(0);
            setGroupStartingItemIndex(null);
            setNavigationDirection("down");
        },
        /** Sets currentItemIndex to the last item's index */ focusLastItem: ()=>{
            setCurrentIndex(items.length - 1);
            setNavigationDirection("up");
            const lastItem = items[items.length - 1];
            if (lastItem.items) {
                setGroupStartingItemIndex(lastItem.items.length - 1);
            }
        },
        clearFocus: ()=>{
            setCurrentIndex(null);
            setGroupStartingItemIndex(null);
        },
        /**
     * Stores the current navigation direction, use this to determine which side to enter a group from.
     * e.g. `navigationDirection === NavigationDirection.DOWN ? groupsFirstIndex : groupsLastIndex`
     */ navigationDirection,
        focusItemByPhrase: pushLetter
    };
}

//# sourceMappingURL=hooks.js.map